import { onUnmounted, ref, watchEffect, type Ref } from "vue";
import { Facade } from "@/drawing";
import type { ObservationEntityRef } from "@/drawing/managers/ObservationsManager";
import {
  observationFrameSources,
  releaseObservationFrames,
  setObservationFrames,
  type ObservationFrameSource,
} from "@/stores/ObservationsPanelState";
import {
  projectWorldBbox,
  useViewSyncedCompute,
} from "@/composable/usePlanFrameProjection";
import type { AnalysisFrameRect } from "@/composable/useAnalysisFrames";
import type { FieldObservation } from "@/types/field-observations.types";
import type { WorldBbox } from "@/utils/plan-analysis/types";
import { unionBbox, zoomWindowAround } from "@/utils/observation-plan";

/** Marge visuelle autour de l'emprise de l'observation (px écran). */
const FRAME_PADDING_PX = 6;

/**
 * Cadres BLEUS des observations sur le plan, projetés en coordonnées écran :
 * un par observation qui a des entités sur ce plan, avec sa lettre. Les
 * emprises monde viennent de l'onglet affiché (`useObservationFramesFeed`) ;
 * ici on ne fait que les recaler sur la vue.
 */
export function useObservationFrames(): { frames: Ref<AnalysisFrameRect[]> } {
  const frames = ref<AnalysisFrameRect[]>([]);

  const compute = (): void => {
    if (!Facade.isInitialized) {
      frames.value = [];
      return;
    }
    frames.value = observationFrameSources.value.map((source) => ({
      id: source.id,
      number: 0,
      label: source.label,
      tone: "observation" as const,
      status: "pending" as const,
      isCurrent: source.isCurrent,
      manual: false,
      assigned: source.assigned,
      ...projectWorldBbox(source.bbox, FRAME_PADDING_PX),
    }));
  };

  useViewSyncedCompute(compute, [observationFrameSources]);

  return { frames };
}

/**
 * Côté volet : fournit à la surcouche du plan un cadre par observation qui a
 * des entités lisibles sur ce plan, tant que l'onglet appelant est affiché.
 * Commun aux onglets Observations (cadre de la carte ouverte mis en avant) et
 * Parties (cadres teintés selon la partie sélectionnée).
 */
export function useObservationFramesFeed(options: {
  observations: Ref<FieldObservation[] | undefined>;
  /** Lettre de chaque observation, par id. */
  letters: Ref<Map<number, string>>;
  entitiesByObservation: Ref<Map<string, ObservationEntityRef[]>>;
  isCurrent: (observation: FieldObservation) => boolean;
  /** Mode association (onglet Parties) — cf. `ObservationFrameSource.assigned`. */
  assigned?: (observation: FieldObservation) => boolean | null;
}): {
  /** Emprise sur le plan des entités d'une observation, null si aucune lisible. */
  bboxOf: (observation: FieldObservation) => WorldBbox | null;
  /** Cadre le plan sur une observation, sans toucher à la sélection. */
  zoomOn: (observation: FieldObservation) => void;
} {
  const owner = Symbol("observation-frames");

  const bboxOf = (observation: FieldObservation): WorldBbox | null =>
    observation.localId
      ? unionBbox(
          (
            options.entitiesByObservation.value.get(observation.localId) ?? []
          ).map((entity) => entity.bbox),
        )
      : null;

  /**
   * Zoom SANS sélectionner les entités : le cadre bleu et le projecteur
   * suffisent à montrer l'observation. Les sélectionner ferait passer le
   * bouton de création à « avec N éléments » et la création suivante les lui
   * retirerait (une entité n'appartient qu'à une observation).
   */
  const zoomOn = (observation: FieldObservation): void => {
    const bbox = bboxOf(observation);
    if (!bbox || !Facade.isInitialized) return;
    const view = zoomWindowAround(bbox);
    Facade.view.zoomWindow(view.minX, view.minY, view.maxX, view.maxY);
    // La sélection en cours est gardée : sa grille (bbox, poignées) a été
    // dessinée avant le zoom, à des coordonnées écran désormais périmées.
    Facade.selection.redrawGrid();
  };

  watchEffect(() => {
    const sources: ObservationFrameSource[] = [];
    for (const observation of options.observations.value ?? []) {
      const bbox = bboxOf(observation);
      if (!observation.localId || !bbox) continue;
      sources.push({
        id: observation.localId,
        label: options.letters.value.get(observation.id) ?? "",
        bbox,
        isCurrent: options.isCurrent(observation),
        assigned: options.assigned?.(observation) ?? null,
      });
    }
    setObservationFrames(sources, owner);
  });

  onUnmounted(() => releaseObservationFrames(owner));

  return { bboxOf, zoomOn };
}
